/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-undef */
import { useMemo } from "react";

import { Box, Button, Center, HStack, Image, Text } from "@chakra-ui/react";

import { BsInfoCircle } from "react-icons/bs";

import { useParams } from "react-router-dom";

import { Permission } from "@/jenesys-types/jenesys-entities/permissions";

import { MoreInfo } from "@/components/routes/company/MoreInfo";
import { useRoles } from "@/hooks/util/useRoles";
import { accountingPlatforms } from "@/util/helpers";

interface Props {
  platform: (typeof accountingPlatforms)[0];
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnectingIntegration: boolean;
  accountingStatus: any;
  handleDisconnect: () => void;
  handleInitiateConnect: () => void;
  category: string;
}

export function Overview({
  platform,
  isConnected,
  isConnecting,
  isDisconnectingIntegration,
  accountingStatus,
  handleDisconnect,
  handleInitiateConnect,
  category
}: Props) {
  const { userCan } = useRoles();
  const { companyId } = useParams();

  const canConnectOrDisconnect = useMemo(() => {
    if (companyId) return true;
    return userCan(`company.connections.${category}` as Permission);
  }, [userCan, companyId, category]);

  const isEmailPlatform = category === "email";

  return (
    <HStack mt={6} justify="space-between">
      <HStack spacing={4}>
        <Image
          alt={platform.name}
          src={platform.imageLink}
          width={50}
          height={50}
        />
        <Box>
          <HStack spacing={2}>
            <Text fontWeight={600}>{platform.displayName}</Text>
            {isEmailPlatform && (
              <MoreInfo
                text="This will capture anything in the subject line that says bill, invoice, or receipt."
                icon={BsInfoCircle}
              />
            )}
          </HStack>
          {accountingStatus?.tenant_name && (
            <Text fontSize={14} fontWeight={500}>
              {`Connected to ${platform.displayName}`}
              {accountingStatus?.tenant_name &&
                `: ${accountingStatus?.tenant_name}`}
              {accountingStatus?.initially_connected_user?.first_name &&
                accountingStatus?.initially_connected_user?.last_name &&
                ` by ${accountingStatus?.initially_connected_user?.first_name} ${accountingStatus?.initially_connected_user?.last_name}`}
            </Text>
          )}
          <Text maxW="lg" fontSize={12}>
            {platform.description}
          </Text>
        </Box>
      </HStack>
      {canConnectOrDisconnect && (
        <Box>
          {isConnected ? (
            platform.disconnectImage ? (
              <Image
                pointerEvents={isDisconnectingIntegration ? "none" : "initial"}
                cursor="pointer"
                opacity={isDisconnectingIntegration ? 0.4 : 1}
                alt="Disconnect"
                src={platform.disconnectImage}
                height={43}
                onClick={handleDisconnect}
              />
            ) : (
              <Button
                isLoading={isDisconnectingIntegration}
                onClick={handleDisconnect}
                height={43}
                bg={platform.secondaryButtonColor}
                color={platform.primaryButtonColor}
                loadingText="Disconnecting..."
                variant="default"
                size="sm"
                minW={222}
                pl={3}
                justifyContent="flex-start"
                rounded={4}
                iconSpacing={3}
                leftIcon={
                  <Center flexShrink={0} p={1} rounded="full" bg="white">
                    <Image
                      alt={platform.name}
                      src={platform.imageLink}
                      width={6}
                      height={6}
                    />
                  </Center>
                }
              >
                Disconnect from {platform.displayName}
              </Button>
            )
          ) : platform.connectImage ? (
            <Image
              cursor="pointer"
              pointerEvents={
                isConnecting || platform.isDisabled ? "none" : "initial"
              }
              opacity={isConnecting || platform.isDisabled ? 0.4 : 1}
              alt="Connect"
              src={platform.connectImage}
              width={222}
              height={43}
              onClick={handleInitiateConnect}
            />
          ) : (
            <Button
              h={43}
              variant="default"
              colorScheme="default"
              _hover={{ bg: platform.primaryButtonColor }}
              color="white"
              isDisabled={platform.isDisabled}
              isLoading={isConnecting}
              loadingText="Connecting..."
              onClick={handleInitiateConnect}
              bg={platform.primaryButtonColor}
              minW={222}
              pl={3}
              justifyContent="flex-start"
              rounded={4}
              iconSpacing={4}
              leftIcon={
                <Center p={1} boxSize={7} rounded="full" bg="white">
                  <Image
                    alt={platform.name}
                    src={platform.imageLink}
                    width={6}
                    height={6}
                  />
                </Center>
              }
              size="sm"
            >
              Connect to {platform.displayName}
            </Button>
          )}
        </Box>
      )}
    </HStack>
  );
}
